import clsx from 'clsx';
/**
 * Originally taken from `react-step-wizard`
 * Modified to fit our use case.
 * https://github.com/jcmcneal/react-step-wizard
 */
import React, { CSSProperties, PureComponent } from 'react';

import Animate from './animate.module.css';
import Step from './Step';
import styles from './styles.module.css';

interface StepWizardProps {
  className: string;

  hashKey?: string;
  initialStep: number;
  instance: (wizard: StepWizardProps) => void;
  isHashEnabled: boolean;
  isLazyMount: boolean;
  nav: (props: StepWizardChildProps) => JSX.Element;

  onStepChange: (data: { previousStep: number; activeStep: number }) => void;
  onNext?: (step: number) => void;

  transitions?: {
    intro?: string;
    enterRight?: string;
    enterLeft?: string;
    exitRight?: string;
    exitLeft?: string;
  };
  style?: CSSProperties;
}

export interface StepWizardChildProps
  extends Pick<StepWizardProps, 'transitions'> {
  isActive?: boolean;
  children?: React.ReactNode;
  currentStep: number;
  totalSteps: number;
  firstStep: () => void;
  lastStep: () => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  hashKey?: string;
  valid?: boolean;
  onValidate?: (bool: boolean) => void;
}

export default class StepWizard extends PureComponent<StepWizardProps, any> {
  static defaultProps = {
    children: [],
    className: null,
    initialStep: 1,
    instance: () => {},
    isHashEnabled: false,
    isLazyMount: false,
    nav: null,
    onStepChange: () => {},
    transitions: undefined,
  };

  constructor(props) {
    super(props);

    this.state = this.initialState();
  }

  componentDidMount() {
    // Hash change listener - for back/forward button
    if (this.props.isHashEnabled) {
      window.addEventListener('hashchange', this.onHashChange);
    }

    // Provide instance to parent
    this.props.instance(this as any);
  }

  componentWillUnmount() {
    // Remove listener
    if (this.props.isHashEnabled) {
      window.removeEventListener('hashchange', this.onHashChange);
    }
  }

  /** Setup Steps */
  initialState = () => {
    const state = {
      activeStep: 0,
      classes: {},
      hashKeys: {},
      valid: {},
    };

    // Set initial classes
    // Get hash only in client side
    const hash = typeof window === 'object' ? this.getHash() : '';
    const children = React.Children.toArray(this.props.children);
    children.forEach((child, i) => {
      if (React.isValidElement<StepWizardChildProps>(child)) {
        // Create hashKey map
        state.hashKeys[i] = child.props.hashKey || `step${i + 1}`;
        state.hashKeys[state.hashKeys[i]] = i;
      } else {
        console.error('Expecting StepWizard children to be of Step components');
      }
    });

    // Set activeStep to initialStep if exists
    const initialStep = this.props.initialStep - 1;
    if (initialStep && children[initialStep]) {
      state.activeStep = initialStep;
    }

    // Set activeStep from hash - trumps initialStep
    if (
      this.props.isHashEnabled &&
      hash &&
      state.hashKeys[hash] !== undefined
    ) {
      // References hashKey
      state.activeStep = state.hashKeys[hash];
    }

    // Give initial step an intro class
    if (this.props.transitions) {
      state.classes[state.activeStep] = this.props.transitions.intro || '';
    }

    return state;
  };

  // Get hash and remove #
  getHash = () => decodeURI(window.location.hash).replace(/^#/, '');

  getTransitions = () =>
    this.props.transitions || {
      enterRight: `${Animate.animated} ${Animate.fadeInRight}`,
      enterLeft: `${Animate.animated} ${Animate.fadeInLeft}`,
      exitRight: `${Animate.animated} ${Animate.fadeOutRight}`,
      exitLeft: `${Animate.animated} ${Animate.fadeOutLeft}`,
    };

  onHashChange = () => {
    const next = this.state.hashKeys[this.getHash()];

    if (next !== undefined) this.setActiveStep(next);
  };

  isInvalidStep = (next) =>
    next < 0 || next >= React.Children.toArray(this.props.children).length;

  setActiveStep = (next) => {
    const active = this.state.activeStep;
    if (active === next) return;
    if (this.isInvalidStep(next)) {
      console.error(`${next + 1} is an invalid step`);
      return;
    }

    const { classes } = this.state;
    const transitions = this.getTransitions();

    if (active < next) {
      // slide left
      classes[active] = transitions.exitLeft;
      classes[next] = transitions.enterRight;
    } else {
      // slide right
      classes[active] = transitions.exitRight;
      classes[next] = transitions.enterLeft;
    }

    this.setState(
      {
        activeStep: next,
        classes,
      },
      () => {
        // Step change callback
        this.onStepChange({
          previousStep: active + 1,
          activeStep: next + 1,
        });
      },
    );
  };

  onStepChange = (stats) => {
    // User callback
    this.props.onStepChange(stats);

    // Update hash if prop set
    if (this.props.isHashEnabled) this.updateHash(this.state.activeStep);
  };

  /** Go to first step */
  firstStep = () => this.goToStep(1);

  /** Go to last step */
  lastStep = () =>
    this.goToStep(React.Children.toArray(this.props.children).length);

  /** Next Step */
  nextStep = () => {
    this.setActiveStep(this.state.activeStep + 1);

    if (typeof this.props.onNext === 'function') {
      this.props.onNext(this.state.activeStep + 1);
    }
  };

  /** Previous Step */
  previousStep = () => this.setActiveStep(this.state.activeStep - 1);

  /** Go to step index */
  goToStep = (step) => this.setActiveStep(step - 1);

  updateHash = (activeStep) => {
    window.location.hash = this.state.hashKeys[activeStep];
  };

  // Allows for using HTML elements as a step
  isReactComponent = (element: any): element is React.ReactElement =>
    element &&
    (typeof element.type === 'function' || typeof element.type === 'object');

  handleValidate = (valid: boolean) => {
    this.setState({
      valid: {
        ...this.state.valid,
        [this.state.activeStep + 1]: valid,
      },
    });
  };

  /** Render */
  render() {
    const currentStep = this.state.activeStep + 1;

    const props: StepWizardChildProps = {
      currentStep,
      totalSteps: React.Children.toArray(this.props.children).length,
      /** Functions */
      nextStep: this.nextStep,
      previousStep: this.previousStep,
      goToStep: this.goToStep,
      firstStep: this.firstStep,
      lastStep: this.lastStep,
      valid: this.state.valid[currentStep],
      onValidate: this.handleValidate,
    };

    const { classes } = this.state;
    const childrenWithProps = React.Children.map(
      this.props.children,
      (child, i) => {
        const childProps = {
          ...props,
          isActive: i === this.state.activeStep,
          transitions: classes[i],
        };

        // Not Lazy Mount || isLazyMount && isActive
        if (
          !this.props.isLazyMount ||
          (this.props.isLazyMount && childProps.isActive)
        ) {
          return (
            <Step {...childProps}>
              {this.isReactComponent(child)
                ? React.cloneElement(child, childProps)
                : child}
            </Step>
          );
        }

        return null;
      },
    );

    return (
      <>
        <div
          className={clsx(styles['step-wrapper'], this.props.className)}
          style={this.props.style}
        >
          {childrenWithProps}
        </div>
        {typeof this.props.nav === 'function' && this.props.nav(props)}
      </>
    );
  }
}
